import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CaretLeft, User, EnvelopeSimple, Phone, IdentificationCard, Crown, CheckCircle, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIER_LABEL = { standard: "Standard", silver: "Silver", gold: "Gold", platinum: "Platinum VIP" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    try {
      const res = await api.get(`/customers/${id}/profile`);
      setData(res.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  if (loading) return <div className="text-muted-foreground">Loading profile…</div>;
  if (!data) return <div className="text-destructive">Customer not found.</div>;

  const { customer, bookings, stats, tickets = [], tolls = [], insurance_cases = [], invoices = [] } = data;
  const fullName = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;

  const payTicket = async (t) => {
    try { await api.post(`/tickets/${t.id}/pay`); toast.success("Marked paid"); await loadProfile(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const payAllTickets = async () => {
    try {
      const res = await api.post("/tickets/pay-all", { customer_id: customer.id });
      toast.success(`Paid ${res.data.paid} ticket(s)`);
      await loadProfile();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unpaidTickets = tickets.filter((t) => t.status !== "paid");

  const payToll = async (t) => {
    try { await api.post(`/tolls/${t.id}/pay`); toast.success("Marked paid"); await loadProfile(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const payAllTolls = async () => {
    try {
      const res = await api.post("/tolls/pay-all", { customer_id: customer.id });
      toast.success(`Paid ${res.data.paid} toll(s)`);
      await loadProfile();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unpaidTolls = tolls.filter((t) => t.status !== "paid");
  const fmtTrip = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="space-y-8" data-testid="customer-profile-page">
      <Button variant="ghost" onClick={() => navigate("/customers")} data-testid="back-to-customers">
        <CaretLeft size={14} className="mr-1" /> Back to customers
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Identity */}
        <div className="border border-border bg-card p-8 lg:col-span-2">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-secondary border border-border flex items-center justify-center">
              <User size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <div className="label-mono">Profile</div>
              <h2 className="font-heading text-3xl mt-1">{fullName || "—"}</h2>
              {customer.company && <div className="text-muted-foreground mt-1">{customer.company}</div>}
              {customer.vip_tier && customer.vip_tier !== "standard" && (
                <div className="flex items-center gap-1.5 mt-3 text-primary"><Crown size={14} weight="fill" /><span className="label-mono">{TIER_LABEL[customer.vip_tier] || customer.vip_tier}</span></div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <Field icon={EnvelopeSimple} label="Email" value={customer.email} />
            <Field icon={Phone} label="Phone" value={customer.phone} />
            <Field icon={IdentificationCard} label="License" value={customer.license_number} />
            <Field label="Nationality" value={customer.nationality} />
            <Field label="DOB" value={customer.date_of_birth} />
            <Field label="Address" value={customer.address} />
          </div>
          {customer.notes && (
            <div className="mt-6">
              <div className="label-mono mb-2">Notes</div>
              <div className="text-sm text-muted-foreground whitespace-pre-wrap">{customer.notes}</div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="border border-border bg-card p-8">
          <div className="label-mono mb-6">Lifetime statistics</div>
          <div className="font-heading text-5xl tracking-tight font-light text-primary" data-testid="lifetime-value">{money(stats.lifetime_value)}</div>
          <div className="text-xs text-muted-foreground mt-1">total lifetime revenue</div>
          <div className="grid grid-cols-2 gap-3 mt-8">
            <Stat label="Bookings" value={stats.total_bookings} testid="ltv-bookings" />
            <Stat label="Completed" value={stats.completed} testid="ltv-completed" />
          </div>
          {(stats.tickets_unpaid_amount > 0 || stats.tickets_total_amount > 0) && (
            <div className="mt-4 border border-border p-4" data-testid="profile-ticket-summary">
              <div className="label-mono">Tickets owed</div>
              <div className="font-heading text-2xl mt-1 text-destructive">{money(stats.tickets_unpaid_amount)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{stats.tickets_unpaid_count} unpaid · {money(stats.tickets_total_amount)} lifetime</div>
            </div>
          )}
          {(stats.tolls_unpaid_amount > 0 || stats.tolls_total_amount > 0) && (
            <div className="mt-4 border border-border p-4" data-testid="profile-toll-summary">
              <div className="label-mono">407 tolls owed</div>
              <div className="font-heading text-2xl mt-1 text-destructive">{money(stats.tolls_unpaid_amount)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{stats.tolls_unpaid_count} unpaid · {money(stats.tolls_total_amount)} lifetime</div>
            </div>
          )}
        </div>
      </div>

      {/* Tickets */}
      {tickets.length > 0 && (
        <div className="border border-border bg-card" data-testid="profile-tickets">
          <div className="px-6 py-5 border-b border-border flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="label-mono">Tickets</div>
              <h3 className="font-heading text-xl mt-1">Traffic & parking tickets</h3>
            </div>
            {unpaidTickets.length > 0 && (
              <Button size="sm" variant="outline" onClick={payAllTickets} data-testid="profile-pay-all-tickets">
                <CheckCircle size={14} className="mr-1.5" /> Pay all ({unpaidTickets.length})
              </Button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 label-mono font-normal">Ticket #</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Type</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Date</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Vehicle</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Amount</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Status</th>
                <th className="text-right px-4 py-3 label-mono font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} className="border-b border-border/40" data-testid={`profile-ticket-${t.id}`}>
                  <td className="px-4 py-3">{t.ticket_number || "—"}</td>
                  <td className="px-4 py-3 capitalize">{t.ticket_type}</td>
                  <td className="px-4 py-3 text-xs">{t.violation_datetime ? new Date(t.violation_datetime).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="px-4 py-3 text-xs">{t.vehicle_label || t.vehicle_plate || "—"}</td>
                  <td className="px-4 py-3 font-heading">{money(t.amount)}</td>
                  <td className="px-4 py-3">{t.status === "paid" ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Paid</Badge> : <Badge variant="destructive">Unpaid</Badge>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {t.photo_url && (
                        <a href={`${BACKEND}${t.photo_url}`} target="_blank" rel="noreferrer" title="View photo">
                          <Button size="icon" variant="ghost"><ImageIcon size={15} /></Button>
                        </a>
                      )}
                      {t.status !== "paid" && (
                        <Button size="icon" variant="ghost" title="Mark paid" onClick={() => payTicket(t)} data-testid={`profile-pay-ticket-${t.id}`}>
                          <CheckCircle size={15} className="text-emerald-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 407 Tolls */}
      {tolls.length > 0 && (
        <div className="border border-border bg-card" data-testid="profile-tolls">
          <div className="px-6 py-5 border-b border-border flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="label-mono">407 Tolls</div>
              <h3 className="font-heading text-xl mt-1">Highway 407 trips</h3>
            </div>
            {unpaidTolls.length > 0 && (
              <Button size="sm" variant="outline" onClick={payAllTolls} data-testid="profile-pay-all-tolls">
                <CheckCircle size={14} className="mr-1.5" /> Pay all ({unpaidTolls.length})
              </Button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 label-mono font-normal">Trip date</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Route</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Vehicle</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Amount</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Status</th>
                <th className="text-right px-4 py-3 label-mono font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tolls.map((t) => (
                <tr key={t.id} className="border-b border-border/40" data-testid={`profile-toll-${t.id}`}>
                  <td className="px-4 py-3 text-xs">{fmtTrip(t.trip_datetime)}</td>
                  <td className="px-4 py-3 text-xs">{[t.entry_point, t.exit_point].filter(Boolean).join(" → ") || "—"}</td>
                  <td className="px-4 py-3 text-xs">{t.vehicle_label || t.vehicle_plate || "—"}</td>
                  <td className="px-4 py-3 font-heading">{money(t.amount)}</td>
                  <td className="px-4 py-3">{t.status === "paid" ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Paid</Badge> : <Badge variant="destructive">Unpaid</Badge>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {t.status !== "paid" && (
                        <Button size="icon" variant="ghost" title="Mark paid" onClick={() => payToll(t)} data-testid={`profile-pay-toll-${t.id}`}>
                          <CheckCircle size={15} className="text-emerald-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Booking history */}
      <div className="border border-border bg-card">
        <div className="px-6 py-5 border-b border-border">
          <div className="label-mono">Booking history</div>
          <h3 className="font-heading text-xl mt-1">All engagements</h3>
        </div>
        {bookings.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No bookings yet for this customer.</div>
        ) : (
          <table className="w-full text-sm" data-testid="customer-bookings-table">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 label-mono font-normal">Service</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Vehicle</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Period</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Total</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-border/40 hover:bg-secondary/30 cursor-pointer transition-colors" onClick={() => navigate(`/bookings/view/${b.id}`)} data-testid={`profile-booking-${b.id}`}>
                  <td className="px-4 py-3 capitalize">{b.booking_type}{b.booking_category ? ` · ${b.booking_category}` : ""}</td>
                  <td className="px-4 py-3">{b.vehicle_label || b.tour_name || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {b.start_datetime ? new Date(b.start_datetime).toLocaleDateString() : "—"} → {b.end_datetime ? new Date(b.end_datetime).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 font-heading">{money(b.total || b.amount || 0)}</td>
                  <td className="px-4 py-3"><span className="label-mono text-primary">{b.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Insurance replacement cases */}
      {insurance_cases.length > 0 && (
        <div className="border border-border bg-card" data-testid="profile-insurance-cases">
          <div className="px-6 py-5 border-b border-border">
            <div className="label-mono">Insurance</div>
            <h3 className="font-heading text-xl mt-1">Insurance replacement cases</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 label-mono font-normal">Case</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Insurer / claim</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Vehicle</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Period</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Stage</th>
              </tr>
            </thead>
            <tbody>
              {insurance_cases.map((c) => (
                <tr key={c.id} className="border-b border-border/40 hover:bg-secondary/30 cursor-pointer transition-colors" onClick={() => navigate(`/insurance/${c.id}`)}>
                  <td className="px-4 py-3 font-heading">{c.case_number}</td>
                  <td className="px-4 py-3 text-xs">{c.insurance_company || "—"}{c.claim_number ? ` · ${c.claim_number}` : ""}</td>
                  <td className="px-4 py-3">{c.assigned_vehicle_label || "—"}</td>
                  <td className="px-4 py-3 text-xs">{(c.rental_start || "").slice(0, 10) || "—"} → {(c.rental_end || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3"><span className="label-mono text-primary">{c.stage}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invoices */}
      {invoices.length > 0 && (
        <div className="border border-border bg-card" data-testid="profile-invoices">
          <div className="px-6 py-5 border-b border-border">
            <div className="label-mono">Billing</div>
            <h3 className="font-heading text-xl mt-1">Invoices</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 label-mono font-normal">Invoice #</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Issued</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Total</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Balance</th>
                <th className="text-left px-4 py-3 label-mono font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-border/40 hover:bg-secondary/30 cursor-pointer transition-colors" onClick={() => navigate("/invoices")}>
                  <td className="px-4 py-3">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-xs">{inv.issue_date || "—"}</td>
                  <td className="px-4 py-3 font-heading">{money(inv.total)}</td>
                  <td className="px-4 py-3 font-heading">{money(inv.balance)}</td>
                  <td className="px-4 py-3">{inv.status === "Paid" ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Paid</Badge> : <Badge variant={inv.status === "Partial" ? "secondary" : "destructive"}>{inv.status}</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="label-mono mb-1.5 flex items-center gap-1.5">{Icon && <Icon size={12} />}{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-border p-4">
      <div className="label-mono">{label}</div>
      <div className="font-heading text-2xl mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}
